import { FC } from "react";
import { useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { schemaEmail } from "../scheme";
import { styles } from "../stylesSignUp";
import { useJoinWaitlistMutation } from "@/store/api/authApi";
import CustomInput from "@/components/UI/CustomInput/CustomInput";
import HeaderControl from "./HeaderControl";
import TitleBlock from "./TitleBlock";

type FormInput = {
  email: string;
};

interface JoinWaitlistProps {
  setSignUpStep: React.Dispatch<React.SetStateAction<number>>;
  setWaitlistEmail: (email: string) => void;
}

const JoinWaitlist: FC<JoinWaitlistProps> = ({ setSignUpStep, setWaitlistEmail }) => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<FormInput>({
    resolver: yupResolver(schemaEmail),
    mode: "all",
  });

  const [joinWaitlist, { isLoading }] = useJoinWaitlistMutation();
  const emailValue = watch("email");

  const onSubmit = async (data: FormInput) => {
    if (!isValid || isLoading) return;
    try {
      const response = await joinWaitlist({ email: data.email }).unwrap();
      setWaitlistEmail(response.email ?? data.email);
      setSignUpStep(6);
    } catch {
      setWaitlistEmail(data.email);
      setSignUpStep(6);
    }
  };

  return (
    <Box sx={styles.wrapper}>
      <HeaderControl onClick={() => setSignUpStep(0)} logo />

      <Box sx={{ marginTop: "32px" }}>
        <TitleBlock
          title={"Request early access"}
          subtitle="Don't have an invite code? Leave your email and we'll reach out when a spot opens up for you."
        />

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: "100%", marginTop: "44px" }}
        >
          <Box sx={{ marginBottom: "42px" }}>
            <CustomInput
              label="Email"
              placeholder="Enter your email"
              type="email"
              register={register}
              name="email"
              value={emailValue}
              error={!!errors.email}
              helperText={errors.email?.message ? "Please enter a valid email address" : ""}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!isValid || isLoading}
            sx={styles.inviteButton}
          >
            Add me to the waitlist
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default JoinWaitlist;
